// Vertical drag handle on the right edge of a resizable rail. Drag writes the
// rail's CSS variable straight to the DOM so layout tracks the pointer without
// React reconciling every mousemove; state + localStorage commit on mouseup.
// Double-click resets to the rail's default width.

import { useEffect, useRef } from 'react';
import type { Rail } from './railWidth';

export function RailResizer({
  rail,
  label,
  className = 'ws-rail-resize',
}: {
  rail: Rail;
  /** Announced to screen readers, e.g. "Resize project tree". */
  label: string;
  className?: string;
}) {
  const handleRef = useRef<HTMLDivElement>(null);
  const { setWidth, resetToDefault, widthRef } = rail.useWidth();
  const { cssVar, min, minBody } = rail.spec;

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    let dragging = false;
    let startX = 0;
    let startWidth = 0;

    function onDown(e: MouseEvent) {
      e.preventDefault();
      dragging = true;
      startX = e.clientX;
      startWidth = widthRef.current;
      handle?.classList.add('is-dragging');
      document.body.classList.add('rail-resizing');
    }
    function onMove(e: MouseEvent) {
      if (!dragging) return;
      // Handle sits on the rail's right edge: dragging right widens it.
      const delta = e.clientX - startX;
      const max = Math.max(min + 1, window.innerWidth - minBody);
      const next = Math.max(min, Math.min(startWidth + delta, max));
      document.documentElement.style.setProperty(cssVar, `${next}px`);
      widthRef.current = next;
    }
    function onUp() {
      if (!dragging) return;
      dragging = false;
      handle?.classList.remove('is-dragging');
      document.body.classList.remove('rail-resizing');
      setWidth(widthRef.current);
    }

    handle.addEventListener('mousedown', onDown);
    handle.addEventListener('dblclick', resetToDefault);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      handle.removeEventListener('mousedown', onDown);
      handle.removeEventListener('dblclick', resetToDefault);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [setWidth, resetToDefault, widthRef, cssVar, min, minBody]);

  return (
    <div
      ref={handleRef}
      className={className}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      title="Drag to resize · double-click to reset"
    />
  );
}
